/**
 * Types du studio création Unquiz — miroir du modèle du jeu
 * (unquizz-v2/src/types/quiz.ts) stocké dans
 * clients/{clientID}/games/unquiz/quizzes/{quizId}.
 */
export type StudioQuestionType = 'qcu' | 'qcm' | 'vf';

export interface StudioQuestion {
    id: string;
    type: StudioQuestionType;
    question: string;
    options: string[];
    correctAnswer?: number; // qcu / vf
    correctAnswers?: number[]; // qcm
    duration: number; // secondes
    anecdote?: string;
    imageUrl?: string;
    /**
     * Mode de jeu dans le Plateau : absent = automatique (le jeu place lui-même
     * quelques Éclair et une Mise), « none » = classique imposé.
     */
    mechanic?: 'eclair' | 'mise' | 'none';
}

export interface StudioQuiz {
    id: string;
    title: string;
    description?: string;
    category?: string;
    coverImage?: string;
    questions: StudioQuestion[];
    scoringType: 'speed' | 'classic';
    lossEnabled: boolean;
    showOnHomepage?: boolean;
}

/** Catégories de quiz (slugs partagés avec le jeu). */
export const QUIZ_CATEGORIES: { id: string; label: string }[] = [
    { id: 'entertainment', label: 'Divertissement' },
    { id: 'culture', label: 'Culture générale' },
    { id: 'music', label: 'Musique' },
    { id: 'sports', label: 'Sport' },
    { id: 'geography', label: 'Géographie' },
    { id: 'history', label: 'Histoire' },
    { id: 'other', label: 'Autre' },
];

export const newQuestionId = () => `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
export const newQuizId = () => `unq-${Date.now().toString(36)}`;

export function emptyQuestion(): StudioQuestion {
    return {
        id: newQuestionId(),
        type: 'qcu',
        question: '',
        options: ['', '', '', ''],
        correctAnswer: 0,
        duration: 20,
    };
}

export function emptyQuiz(): StudioQuiz {
    return {
        id: newQuizId(),
        title: '',
        questions: [emptyQuestion()],
        scoringType: 'speed',
        lossEnabled: false,
        showOnHomepage: true,
    };
}

/** Une question est jouable si le texte, toutes les options et la bonne réponse sont là. */
export function questionIsComplete(q: StudioQuestion): boolean {
    if (!q.question.trim()) return false;
    if (q.options.some((o) => !o.trim())) return false;
    if (q.type === 'qcm') return (q.correctAnswers?.length ?? 0) >= 1;
    return q.correctAnswer !== undefined && q.correctAnswer >= 0 && q.correctAnswer < q.options.length;
}

export function quizIsPlayable(quiz: StudioQuiz): boolean {
    return quiz.title.trim().length > 0 && quiz.questions.length >= 1 && quiz.questions.every(questionIsComplete);
}

/** Ce qui empêche d'enregistrer, dit précisément (au lieu de « tout compléter »). */
export type QuizIssue =
    | { kind: 'title' }
    | { kind: 'noQuestion' }
    | { kind: 'questionText' | 'option' | 'correct'; index: number; questionId: string };

export function firstQuizIssue(quiz: StudioQuiz): QuizIssue | null {
    if (!quiz.title.trim()) return { kind: 'title' };
    if (quiz.questions.length === 0) return { kind: 'noQuestion' };
    for (let index = 0; index < quiz.questions.length; index += 1) {
        const q = quiz.questions[index];
        if (!questionIsComplete(q)) {
            if (!q.question.trim()) return { kind: 'questionText', index, questionId: q.id };
            if (q.options.some((o) => !o.trim())) return { kind: 'option', index, questionId: q.id };
            return { kind: 'correct', index, questionId: q.id };
        }
    }
    return null;
}

export function quizIssueMessage(issue: QuizIssue): string {
    switch (issue.kind) {
        case 'title':
            return 'Donnez un titre au quiz pour l’enregistrer';
        case 'noQuestion':
            return 'Ajoutez au moins une question';
        case 'questionText':
            return `Question ${issue.index + 1} : écrivez la question`;
        case 'option':
            return `Question ${issue.index + 1} : remplissez toutes les réponses`;
        default:
            return `Question ${issue.index + 1} : choisissez la bonne réponse`;
    }
}

/** Document Firestore d'un quiz → modèle de l'éditeur (mêmes valeurs par défaut partout). */
export function studioQuizFromDoc(data: any): StudioQuiz {
    return {
        id: data.id,
        title: data.title ?? '',
        description: data.description ?? undefined,
        category: data.category ?? undefined,
        coverImage: data.coverImage ?? undefined,
        questions: (data.questions ?? []).map((q: StudioQuestion) => ({
            ...q,
            type: q.type ?? 'qcu',
            duration: q.duration ?? 20,
        })),
        scoringType: data.scoringType ?? 'speed',
        lossEnabled: data.lossEnabled ?? false,
        showOnHomepage: data.showOnHomepage ?? true,
    };
}
